import authRoles from 'app/auth/authRoles';
import { FranchiseFeatureType } from 'app/main/apps/franchises/type';
import { VISIBILITY_FEATURE } from 'app/main/apps/visibility/constants';
import { FeatureFlag, isFeatureFlagEnabled } from 'app/utils/featureFlags.utils';

export interface NavigationConfigChildrenType {
  id: string;
  auth?: string[];
  title?: string;
  type?: string;
  icon?: { name: string; source: 'MaterialIcon' | 'FontAwesome' | 'MaterialCommunity' | 'Tiktok' | 'MUI' };
  url?: string;
  target?: string;
  translate?: string;
  badge?: {
    icon?: string;
    title?: string;
    bg?: string;
    fg?: string;
  };
  featureRestriction?: FranchiseFeatureType;
  /** Hidden unless the flag is listed in REACT_APP_FEATURE_FLAGS. */
  featureFlag?: FeatureFlag;
  children?: NavigationConfigChildrenType[];
  canBeDefault?: boolean;
  activeBg?: string;
  /** Collapse only: suppress this item's own badge while expanded, e.g. when its children
   * already show the same counts individually and the total would double up. */
  hideBadgeWhenOpen?: boolean;
}

export interface NavigationConfigType {
  id: string;
  title: string;
  canBeDefault?: boolean;
  translate?: string;
  type: string;
  icon?: { name: string; source: 'MaterialIcon' | 'FontAwesome' | 'MaterialCommunity' | 'Tiktok' | 'MUI' };
  url?: string;
  /** Hidden unless the flag is listed in REACT_APP_FEATURE_FLAGS. */
  featureFlag?: FeatureFlag;
  children?: NavigationConfigChildrenType[];
  badge?: {
    icon?: string;
    title?: string;
    bg?: string;
    fg?: string;
  };
  /** Check if the user has access to the navigation item */
  auth?: string[];
  /** Check if the feature is allowed for a franchise */
  featureRestriction?: FranchiseFeatureType;
}

export const routesUrl = {
  login: '/login',
  scanner: '/stores/:storeId/tags/:id/scanner',
  cgv: '/cgv',
  // The till surface. `posTillEntry` is what Zelty is given: it carries no tag, so the
  // device asks for one and remembers it. Everything POS lives under /pos/ so it can never
  // collide with a dashboard route.
  posTillEntry: '/pos/:posCode',
  posTill: '/pos/:posCode/:tagId',
  // L'addition webview pages: more than two segments, so `/pos/:posCode/:tagId` cannot match them.
  posLadditionSearch: '/pos/posadd/loyalty/search',
  posLadditionOffers: '/pos/posadd/loyalty/offers',
  analytics: '/pages/dashboards/analytics',
  comingSoon: '/pages/coming-soon',
  forgotPassword: '/pages/auth/forgot-password',
  forbidden: '/pages/auth/forbidden',
  offersCalendar: '/pages/offers/calendar',
  loyalties: '/pages/loyalty',
  profile: '/pages/profile',
  stores: '/pages/stores/all',
  storeItem: '/pages/stores/:id',
  storeOpinions: '/pages/stores/:id/opinions',
  satisfaction: '/pages/satisfaction',
  googleReviews: '/pages/google-reviews',
  demoAnalytics: '/pages/demo/analytics',
  demoLoyalties: '/pages/demo/loyalty',
  demoClients: '/pages/demo/clients',
  listClientsPage: '/pages/user/clients',
  crmClientsPage: '/pages/user/crm',
  clientsDetailPage: '/pages/user/clients/:id',
  franchisePage: '/pages/franchise',
  accountConfirmation: '/pages/confirmation',
  campaignCreation: '/pages/campaign/new',
  campaignEdition: '/pages/campaign/:id/edit',
  campaignHistory: '/pages/campaign/history',
  campaignDetail: '/pages/campaign/:id',
  campaignTemplateCreation: '/pages/template/new',
  marketingList: '/pages/marketing/list',
  eMarketingPage: '/pages/e-marketing',
  tabletPage: '/stores/:storeId/tags/:tagId/tablet',
  mobileHome: '/m',
  mobileScanner: '/m/scanner',
  mobileClients: '/m/clients',
  mobileClientDetail: '/m/clients/:clientId',
  mobileOffers: '/m/offers',
  mobileCampaigns: '/m/campaigns',
  mobileCampaignNew: '/m/campaigns/new',
  marketingBirthday: '/pages/marketing/birthday/:id',
  marketingLostCustomer: '/pages/marketing/lost/:id',
  marketingNewCustomer: '/pages/marketing/new-customer/:id',
  administration: '/pages/admin/dashboard',
  owners: '/pages/owners',
  myBrands: '/pages/my-brands',
  franchisePanel: '/pages/admin/franchise-panel/:id',
  gamesRoot: '/pages/games',
  gamesHistory: '/pages/games/history',
  gamesCreation: '/pages/games/new',
  gamesDetail: '/pages/games/:id',
  pronoRoot: '/pages/prono',
  pronoHistory: '/pages/prono/history',
  pronoCreation: '/pages/prono/new',
  pronoDetail: '/pages/prono/:id',
  formsHistory: '/pages/forms',
  formsCreation: '/pages/forms/new',
  formsDetail: '/pages/forms/:id',
  formsFill: '/pages/forms/:id/fill',
  noStores: '/pages/no-stores',
  uberallPage: '/pages/uberall',
  listCat: '/pages/list-category',
  referral: '/pages/referral',
  referralTiers: '/pages/referral/paliers',
  visibility: '/pages/visibility',
  visibilityScore: '/pages/visibility/score',
  visibilitySettings: '/pages/visibility/settings',
  visibilityDashboard: '/pages/visibility/dashboard',
  visibilityReviews: '/pages/visibility/reviews',
  visibilityPosts: '/pages/visibility/posts',
  visibilityAnalytics: '/pages/visibility/analytics',
};

const googleVisibilityEntries: NavigationConfigChildrenType[] = [
  {
    id: 'visibility-score',
    title: 'LittleBill Score',
    canBeDefault: true,
    translate: 'visibility-score',
    type: 'item',
    icon: { name: 'google', source: 'FontAwesome' },
    auth: authRoles.owner,
    featureRestriction: VISIBILITY_FEATURE,
    url: routesUrl.visibilityScore,
  },
  {
    id: 'visibility-settings',
    title: 'Listing settings',
    translate: 'visibility-settings',
    type: 'item',
    icon: { name: 'tune', source: 'MaterialIcon' },
    auth: authRoles.owner,
    featureRestriction: VISIBILITY_FEATURE,
    url: routesUrl.visibilitySettings,
  },
  {
    id: 'visibility-dashboard',
    title: 'Visibility dashboard',
    translate: 'visibility-dashboard',
    type: 'item',
    icon: { name: 'insert_chart_outlined', source: 'MaterialIcon' },
    auth: authRoles.owner,
    featureRestriction: VISIBILITY_FEATURE,
    url: routesUrl.visibilityDashboard,
  },
  {
    id: 'visibility-reviews',
    title: 'Reviews inbox',
    translate: 'visibility-reviews',
    type: 'item',
    icon: { name: 'inbox', source: 'MaterialIcon' },
    auth: authRoles.owner,
    featureRestriction: VISIBILITY_FEATURE,
    url: routesUrl.visibilityReviews,
  },
  {
    id: 'visibility-posts',
    title: 'Google posts',
    translate: 'visibility-posts',
    type: 'item',
    icon: { name: 'announcement', source: 'MaterialIcon' },
    auth: authRoles.owner,
    featureRestriction: VISIBILITY_FEATURE,
    url: routesUrl.visibilityPosts,
  },
  {
    id: 'visibility-analytics',
    title: 'Analytics & local SEO',
    translate: 'visibility-analytics',
    type: 'item',
    icon: { name: 'explore', source: 'MaterialIcon' },
    auth: authRoles.owner,
    featureRestriction: VISIBILITY_FEATURE,
    url: routesUrl.visibilityAnalytics,
  },
];

const isShownByFeatureFlag = (item: { featureFlag?: FeatureFlag }) =>
  !item.featureFlag || isFeatureFlagEnabled(item.featureFlag);

const withoutDisabledFeatureFlags = <
  T extends { featureFlag?: FeatureFlag; children?: NavigationConfigChildrenType[] },
>(
  items: T[],
): T[] =>
  items
    .filter(isShownByFeatureFlag)
    .map(item => (item.children ? { ...item, children: withoutDisabledFeatureFlags(item.children) } : item));

const navigationConfig = withoutDisabledFeatureFlags<NavigationConfigType>([
  {
    id: 'retain',
    title: 'retain',
    translate: 'retain',
    type: 'group',
    auth: authRoles.owner,
    children: [
      {
        id: 'analytics-dashboard-paid',
        title: 'dashboard',
        canBeDefault: true,
        translate: 'dashboard',
        type: 'item',
        icon: { name: 'dashboard', source: 'MaterialIcon' },
        url: routesUrl.analytics,
        auth: authRoles.owner,
      },
      {
        id: 'list-clients',
        title: 'list clients',
        canBeDefault: true,
        translate: 'list-clients',
        type: 'item',
        icon: { name: 'people', source: 'MaterialIcon' },
        url: routesUrl.listClientsPage,
        auth: authRoles.owner,
      },
      {
        id: 'client-journey',
        title: 'client journey',
        translate: 'client-journey',
        type: 'collapse',
        icon: { name: 'timeline', source: 'MaterialIcon' },
        auth: authRoles.owner,
        children: [
          {
            id: 'crm-clients',
            title: 'crm clients',
            translate: 'crm-clients',
            type: 'item',
            url: routesUrl.crmClientsPage,
            icon: { name: 'list_alt', source: 'MaterialIcon' },
            auth: authRoles.fullAccess,
          },
          {
            id: 'list-category',
            title: 'list category',
            canBeDefault: true,
            translate: 'list-category',
            type: 'item',
            icon: { name: 'category', source: 'MaterialIcon' },
            url: routesUrl.listCat,
            auth: authRoles.fullAccess,
          },
          {
            id: 'forms',
            title: 'Formulaires',
            canBeDefault: true,
            translate: 'forms',
            type: 'item',
            icon: { name: 'description', source: 'MaterialIcon' },
            url: routesUrl.formsHistory,
            auth: authRoles.owner,
            featureFlag: 'forms',
          },
        ],
      },
      {
        id: 'loyalty-dashboard',
        title: 'Loyalty',
        canBeDefault: true,
        translate: 'loyalty',
        type: 'item',
        icon: { name: 'loyalty', source: 'MaterialIcon' },
        url: routesUrl.loyalties,
        auth: authRoles.owner,
      },
      {
        id: 'validation',
        title: 'Validation',
        type: 'group',
        auth: authRoles.registered,
        children: [
          {
            id: 'validation-configuration',
            title: 'Validation',
            type: 'item',
            translate: 'configuration',
            url: routesUrl.accountConfirmation,
            auth: authRoles.registered,
          },
        ],
      },
      {
        id: 'no-stores',
        title: 'No Stores',
        canBeDefault: false,
        translate: 'no-stores',
        type: 'item',
        icon: { name: 'store', source: 'MaterialIcon' },
        url: routesUrl.noStores,
        auth: authRoles.noStores,
      },
      {
        id: 'notification-campaign',
        icon: { name: 'message', source: 'MaterialIcon' },
        canBeDefault: true,
        title: 'Campaign',
        translate: 'campaign',
        type: 'item',
        url: routesUrl.campaignHistory,
        auth: authRoles.owner,
      },
      {
        id: 'automatic-marketing',
        icon: { name: 'schedule', source: 'MaterialIcon' },
        canBeDefault: true,
        title: 'Marketing',
        translate: 'marketing-automated',
        type: 'item',
        url: routesUrl.marketingList,
        auth: authRoles.fullAccess,
      },
      {
        id: 'e-marketing',
        title: 'E-Marketing',
        translate: 'e-marketing',
        type: 'item',
        icon: { name: 'devices', source: 'MaterialIcon' },
        url: routesUrl.eMarketingPage,
        auth: authRoles.owner,
      },
      {
        id: 'satisfaction',
        title: 'Satisfaction LittleBill — Avis Google',
        canBeDefault: true,
        translate: 'satisfaction',
        type: 'item',
        icon: { name: 'record_voice_over', source: 'MaterialIcon' },
        auth: authRoles.owner,
        url: routesUrl.googleReviews,
      },
    ],
  },
  {
    id: 'booster',
    title: 'Booster',
    translate: 'booster',
    type: 'group',
    auth: authRoles.owner,
    featureRestriction: FranchiseFeatureType.GAME,
    children: [
      {
        id: 'games',
        title: 'Games',
        canBeDefault: true,
        translate: 'games',
        type: 'item',
        icon: { name: 'games', source: 'MaterialIcon' },
        url: routesUrl.gamesHistory,
        auth: authRoles.owner,
        featureRestriction: FranchiseFeatureType.GAME,
      },
      {
        id: 'prono',
        title: 'Prono',
        canBeDefault: true,
        translate: 'prono',
        type: 'item',
        icon: { name: 'stars', source: 'MaterialIcon' },
        url: routesUrl.pronoHistory,
        auth: authRoles.owner,
        // Prono is always tied to the gift wheel (GAME): having GAME = having Prono.
        featureRestriction: FranchiseFeatureType.GAME,
      },
      {
        id: 'referral',
        title: 'Referral',
        canBeDefault: true,
        translate: 'referral',
        type: 'item',
        icon: { name: 'card_giftcard', source: 'MaterialIcon' },
        auth: authRoles.owner,
        featureRestriction: FranchiseFeatureType.GAME,
        url: routesUrl.referral,
      },
    ],
  },
  {
    id: 'radiate',
    title: 'Radiate',
    translate: 'radiate',
    type: 'group',
    auth: authRoles.owner,
    children: googleVisibilityEntries,
  },
  {
    id: 'profile',
    title: 'Profile',
    translate: 'profile',
    type: 'group',
    auth: authRoles.owner,
    children: [
      {
        id: 'user-profile',
        title: 'Profile',
        translate: 'profile',
        type: 'item',
        icon: { name: 'person', source: 'MaterialIcon' },
        url: routesUrl.profile,
        auth: authRoles.owner,
      },
      {
        id: 'store-infos',
        title: 'Store',
        translate: 'store',
        type: 'item',
        icon: { name: 'store', source: 'MaterialIcon' },
        url: routesUrl.stores,
        auth: authRoles.owner,
      },
      {
        id: 'franchise',
        title: 'Franchise',
        translate: 'franchise',
        type: 'item',
        icon: { name: 'account_balance', source: 'MaterialIcon' },
        url: routesUrl.franchisePage,
        auth: authRoles.fullAccess,
      },
    ],
  },
  {
    id: 'admin',
    title: 'Administration',
    translate: 'administration',
    type: 'group',
    icon: { name: 'lock', source: 'MaterialIcon' },
    auth: authRoles.admin,
    children: [
      {
        id: 'admin-dashboard',
        title: 'Administration',
        translate: 'administration',
        type: 'item',
        icon: { name: 'lock', source: 'MaterialIcon' },
        url: routesUrl.administration,
        auth: authRoles.admin,
      },
    ],
  },
]);

export { navigationConfig };
